import fetchWithRetryBuilder from 'fetch-retry';
import { logWarning, logError } from '../utilities/logging';
import { getCart, ShopifyCart } from './getCart';
import { wait } from '../utilities/wait';

const fetchWithRetry = fetchWithRetryBuilder(fetch);

interface FetchRetryOptions {
  url: string;
  options: RequestInit;
  retries?: number;
  retryDelay?: number;
  onRetryStart?: () => void;
  onRetryEnd?: () => void;
  extraShouldRetryCheck?: ({ cartData }: { cartData: ShopifyCart }) => boolean;
  cleanUp?: () => Promise<boolean>;
}

const unloadError =
  ({
    url,
    options,
    attempts,
    cleanUp
  }: {
    url: string;
    options: RequestInit;
    attempts: number;
    cleanUp: () => Promise<boolean>;
  }) =>
  async () => {
    await cleanUp();
    logError({
      label: 'fetchRetry',
      error: new Error(`Fetch to ${url} timed out after ${attempts} attempts`),
      message: `Fetch to ${url} timed out after ${attempts} attempts`,
      extraData: { url, options, attempts }
    });
  };

export const fetchRetry = async ({
  url,
  options,
  retries = 10,
  retryDelay = 1000,
  onRetryStart,
  onRetryEnd,
  extraShouldRetryCheck,
  cleanUp = () => Promise.resolve(true)
}: FetchRetryOptions): Promise<Response | null> => {
  let result: Response | null = null;
  let attempts = 0;
  let isRetrying = false;
  let extraShouldRetry = false;

  // Store the handler function reference so we can properly remove it later
  const unloadHandler = unloadError({ url, options, attempts, cleanUp });

  // Attach beforeunload event listener to fire error if the fetch hasn't completed before the page is unloaded.
  window.addEventListener('beforeunload', unloadHandler);

  // const requestKey = `fetchRetry:${Date.now()}`;
  // Save the url and payload to local storage so we can retry the request if it fails and the page is reloaded.
  // localStorage.setItem(requestKey, JSON.stringify({ url, options }));
  try {
    result = await fetchWithRetry(url, {
      retries,
      retryDelay,
      ...options,
      retryOn: async (attempt, error, response) => {
        attempts = attempt;
        const statusCode = response?.status || 0;

        if (extraShouldRetryCheck) {
          // Check the cart to see if the updates actually made it to the cart.
          await wait({ ms: 2000 });
          const cartData = await getCart();
          if ('error' in cartData) {
            // If the cart data is an error, we should retry.
            extraShouldRetry = true;
          } else {
            const res = extraShouldRetryCheck({ cartData });
            extraShouldRetry = res;
          }
        }

        // Determine if we should retry:
        // - Network errors (error !== null): connection failures, timeouts, temporary internet loss
        // - 408 Request Timeout: connection timeout
        // - 429 Too Many Requests: rate limiting
        // - 5xx server errors (500-599): server-side issues
        const hasNetworkOrServerError =
          error !== null || statusCode === 408 || statusCode === 429 || (statusCode >= 500 && statusCode < 600);

        // If extraShouldRetryCheck is provided, it gates ALL retries:
        // - If item IS in cart (extraShouldRetry = false), never retry regardless of status code
        // - If item is NOT in cart AND there's an error, retry
        // - If item is NOT in cart but response is 200, DON'T retry (trust the 200)
        // If no extraShouldRetryCheck provided, fall back to normal retry logic (network/server errors only)
        const shouldRetry = extraShouldRetryCheck
          ? extraShouldRetry && hasNetworkOrServerError
          : hasNetworkOrServerError;

        if (shouldRetry) {
          // Notify that retry is starting (only on first retry attempt)
          if (!isRetrying && onRetryStart) {
            isRetrying = true;
            onRetryStart();
          }

          logWarning({
            label: 'fetchRetry',
            warning: `Retrying request to ${url}`,
            message: `Retrying request to ${url} (attempt ${attempt + 1})`,
            extraData: { url, options, attempt, error, response, statusCode }
          });
          return true;
        }

        // Not retrying - notify that retry has ended if it was retrying
        if (isRetrying && onRetryEnd) {
          isRetrying = false;
          onRetryEnd();
        }

        // Remove the beforeunload event listener.
        window.removeEventListener('beforeunload', unloadHandler);
        return false;
      }
    });

    // If we were retrying and the request succeeded, notify that retry has ended
    if (isRetrying && onRetryEnd) {
      isRetrying = false;
      onRetryEnd();
    }

    // Log warning if request succeeded after retries
    if (attempts > 0 && result) {
      logWarning({
        label: 'fetchRetry',
        warning: `Request to ${url} succeeded after ${attempts + 1} attempt(s)`,
        message: `Request to ${url} succeeded after ${attempts + 1} attempt(s)`,
        extraData: { url, options, attempts, finalAttempt: attempts + 1, response: result }
      });
    }
  } catch (error) {
    // If we were retrying and the request failed, notify that retry has ended
    if (isRetrying && onRetryEnd) {
      isRetrying = false;
      onRetryEnd();
    }

    logError({
      label: 'fetchRetry',
      error,
      message: `Failed to fetch ${url}. Attempting fallback`,
      extraData: { url, options, attempts, error }
    });
    // Don't throw so we can gracefully handle errors.
    // throw error;
  } finally {
    // Always remove the beforeunload event listener to prevent memory leaks
    window.removeEventListener('beforeunload', unloadHandler);
  }
  return result;
};

// @TODO: Consider how we can persist the fetch retries in the background after the page reloads.
// Paying attention to the image binary data that needs to be stored in local storage.
// Lets prove this is an issue before we implement it.

// interface BackgroundFetchRetryOptions {
//   url: string;
//   options: RequestInit;
//   retries?: number;
//   retryDelay?: number;
// }

// Used to retry fetch requests in the background after the page reloads.
// export const backgroundFetchRetry = async ({
//   url,
//   options,
//   retries = 5,
//   retryDelay = 1000
// }: BackgroundFetchRetryOptions) => {
//   let attempts = 0;
//   const allFetchRetriesKeys = localStorage.keys().filter((key) => key.startsWith('fetchRetry:'));
//  const retried = Promise.all(allFetchRetriesKeys.map(async (key) => {
//   const fetchRetryItem = localStorage.getItem(key);
//   if (!fetchRetryItem) return;

//     const { url, options } = JSON.parse(localStorage.getItem(fetchRetryItem) || '{}');

//     if (!url || !options) return;

//     try {
//       const result = await fetchWithRetry(url, {
//         retries,
//         retryDelay,
//         ...options,
//         retryOn: (attempt, error, response) => {
//           attempts = attempt;
//           // Retry on any network error, or 4xx but not 5xx status codes.
//           if (error !== null || (response && response.status >= 400 && response.status < 500)) {
//             logWarning({
//               label: 'backgroundFetchRetry',
//               warning: `Retrying request to ${url}`,
//               message: `Retrying request to ${url} (attempt ${attempt + 1})`,
//               extraData: { url, options, attempt, error, response, statusCode: response?.status }
//             });
//             return true;
//           }
//           // Remove the request from local storage so we don't retry it again.
//           localStorage.removeItem(fetchRetryItem);
//           return false;
//         }
//       });
//     } catch (error) {
//       logError({ label: 'backgroundFetchRetry', error, message: `Failed to fetch ${url}`, extraData: { url, options, attempts, error } });
//     }
//   }));
//   return retried;
// };
